/**
 * Purpose: This middleware intercepts SAVE_OFFER to check for incomplete trade-in YMMT.
 * If the feature toggle is enabled and there are included trade-ins with incomplete YMMT,
 * it shows the IncompleteTradeModal and prevents the save until the user acknowledges the modal.
 *
 * Draft saves (action.meta.isDraft === true) bypass all validation checks and proceed immediately.
 * This is intentional - draft saves represent work-in-progress and should save regardless of completeness.
 * Only final offer submissions should be validated for incomplete trades.
 *
 * Users can bypass the check for regular saves by clicking "Continue" in the modal, which sets a bypass flag.
 * The bypass flag is automatically cleared after the save proceeds.
 */
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import { SAVE_OFFER } from '../actionTypes/offerActionTypes';
import { PAYMENT_RECEIVED, PAYMENT_FAILURE } from '../actionTypes/deskingActionTypes';
import { shouldShowIncompleteTradeModalForMultiple } from '../selectors/tradeYmmtSelectors';
import { showIncompleteTradeModal, clearBypassIncompleteTradeCheck } from '../actions/incompleteTradeModalActionCreators';
import * as incompleteTradeModalSelectors from '../selectors/incompleteTradeModalSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { saveOffer } from '../actions/offerActionCreators';

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    switch (action.type) {
        case SAVE_OFFER: {
            // Skip validation for draft saves - drafts should save regardless of completeness
            if (action.meta?.isDraft) {
                break;
            }

            const state = store.getState();
            const bypassCheck = incompleteTradeModalSelectors.getBypassIncompleteTradeCheck(state);

            if (bypassCheck) {
                const dealType = offerReduxSelectors.getCurrentOfferType(state);
                logNewRelicPageAction('MV:Offer Submit - Incomplete Trade', {
                    userContinuedWithoutTrade: true,
                    dealType
                });
                store.dispatch(clearBypassIncompleteTradeCheck());
                break;
            }

            const shouldShowModal = shouldShowIncompleteTradeModalForMultiple(state);

            if (shouldShowModal) {
                const dealType = offerReduxSelectors.getCurrentOfferType(state);
                logNewRelicPageAction('MV:Offer Submit - Incomplete Trade', {
                    userContinuedWithoutTrade: false,
                    dealType
                });
                store.dispatch(showIncompleteTradeModal());
                return;
            }
            break;
        }

        case PAYMENT_RECEIVED: {
            const state = store.getState();
            const bypassCheck = incompleteTradeModalSelectors.getBypassIncompleteTradeCheck(state);
            if (bypassCheck) {
                store.dispatch(saveOffer());
            }
            break;
        }

        case PAYMENT_FAILURE: {
            const state = store.getState();
            const bypassCheck = incompleteTradeModalSelectors.getBypassIncompleteTradeCheck(state);
            if (bypassCheck) {
                store.dispatch(clearBypassIncompleteTradeCheck());
            }
            break;
        }
    }

    return next(action);
};
